import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { DEPOSIT_METRICS } from '../screens/depositMetrics'
import { DepositReceivedToast } from './DepositReceivedToast'

// Long enough to read two lines after a payment sheet has just come down.
const TOAST_VISIBLE_MS = 4000
// Kept in sync with the card's own FadeOutUp, so the overlay outlives it.
const TOAST_EXIT_MS = 350
// The failure banner's place, which this card takes once the sheet is gone.
const TOAST_TOP = 56
const TOAST_SIDE = 14

/** What the card says: the amount that landed, and where it came from. */
export interface DepositReceipt {
  title: string
  detail: string
}

interface DepositReceivedToast {
  /** The overlay to render; the caller owns only where it sits in the tree. */
  depositToast: ReactNode
  showDepositToast: (receipt: DepositReceipt) => void
  clearDepositToast: () => void
}

/**
 * The deposit confirmation that replaces the payment sheet.
 *
 * It is raised as the flow closes and has to outlive it, so it is mounted
 * beside the sheet rather than inside it — and `closeFlow` leaves it alone.
 */
export function useDepositReceivedToast(): DepositReceivedToast {
  const insets = useSafeAreaInsets()
  const [receipt, setReceipt] = useState<DepositReceipt>()
  const [shown, setShown] = useState(false)
  const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const unmountTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const clearDepositToast = useCallback(() => {
    clearTimeout(hideTimer.current)
    clearTimeout(unmountTimer.current)
    setShown(false)
    setReceipt(undefined)
  }, [])

  const showDepositToast = useCallback((next: DepositReceipt) => {
    clearTimeout(hideTimer.current)
    clearTimeout(unmountTimer.current)
    setReceipt(next)
    setShown(true)
    hideTimer.current = setTimeout(() => {
      // Unmounting the card plays its exit; the overlay follows it down.
      setShown(false)
      unmountTimer.current = setTimeout(
        () => setReceipt(undefined),
        TOAST_EXIT_MS,
      )
    }, TOAST_VISIBLE_MS)
  }, [])

  useEffect(() => () => clearDepositToast(), [clearDepositToast])

  const depositToast =
    receipt === undefined ? null : (
      <View
        pointerEvents="none"
        style={[
          styles.overlay,
          { top: insets.top + TOAST_TOP - DEPOSIT_METRICS.reference.insetTop },
        ]}
      >
        {shown ? <DepositReceivedToast {...receipt} /> : null}
      </View>
    )

  return { depositToast, showDepositToast, clearDepositToast }
}

const styles = StyleSheet.create({
  overlay: { position: 'absolute', left: TOAST_SIDE, right: TOAST_SIDE },
})
